import React from 'react';
import PropTypes from 'prop-types';

import MetricLoadingView from '../../../components/MetricLoadingView.jsx';

import Panel from '../../../components/Panel.jsx';

import {
    RIQPopover
} from '../../../components/RIQPopover.jsx';

import {
    useRegisterSlideElementsForPPTExport
} from '../../../hooks/useExport.js';

import {
    useCurationConfigValue
} from '../../../hooks/widgetConfiguration.js';

import {
    useSetExportConfig
} from '../../../hooks/exportConfigurationHooks.js';

import RIQMetricLineChart from '../../../components/RIQMetricLineChart.jsx';
import ViewContentButton from './ViewContentButton.jsx';
import useContentModal from '../hooks/useContentModal.jsx';

import useListeningCurationFilterValues from '../hooks/useListeningCurationFilterValues.js';

import {
    useIntervalOptions,
    IntervalSelector
} from './IntervalSelector.jsx';

import {
    listeningDefinitions
} from './enums/listeningDefinitions.jsx';

function LineChartTooltip({lineConfig, chartConfigs, datum, onTooltipFocusChange, showContentModal, placement}) {

    const metricFilterOverrides = {
        startDate: datum?.newStartDate,
        endDate: datum?.newEndDate
    };

    return (
        <>
            {datum && <div
                className='tlw-pointer-events-auto'
                onMouseEnter={() => onTooltipFocusChange(true)}
                onMouseLeave={() => onTooltipFocusChange(false)}
            >
                <RIQPopover
                    triggerNode={<div/>}
                    className='tlw-flex tlw-w-full tlw-items-center tlw-gap-[10px] tlw-text-md tlw-font-normal'
                    placement={placement || 'top'}
                    portalNode={document.body}
                    additionalHookProps={{
                        visible: true
                    }}
                >
                    <div>
                        <div className='tlw-m-0 tlw-whitespace-nowrap tlw-rounded-t-md tlw-border tlw-border-l-0 tlw-border-r-0 tlw-border-t-0 tlw-border-solid tlw-border-gray-200 tlw-px-7 tlw-py-4 tlw-text-md tlw-text-gray-700'>
                            {datum.tooltipTitle || datum.xValue}
                        </div>

                        <div className='tlw-grid tlw-grid-cols-2 tlw-py-4.5'>

                            {_.map(chartConfigs, (chartConfig, idx) => {
                                return (
                                    <div
                                        key={idx}
                                        className='tlw-border-0 tlw-border-solid tlw-border-gray-200 tlw-px-7 tlw-py-4 tlw-text-left even_tlw-border-l'
                                    >
                                        <div
                                            className='tlw-whitespace-nowrap tlw-text-11 tlw-font-semibold tlw-uppercase tlw-text-gray-700'>
                                            {chartConfig.chartName}
                                        </div>
                                        <div
                                            className='tlw-text-xl tlw-font-bold tlw-leading-36 tlw-text-gray-900'>
                                            {chartConfig.metrics[0].expandedFormatter(datum[chartConfig.metrics[0].dataKey])}
                                        </div>
                                    </div>
                                );
                            })}
                            <div className='tlw-col-span-2 tlw-flex tlw-justify-center'>
                                <ViewContentButton
                                    showContentModal={showContentModal}
                                    metricFilterOverrides={metricFilterOverrides}
                                />
                            </div>
                        </div>

                    </div>
                </RIQPopover>
            </div>}
        </>
    );
}

ActivitySentimentWidget.propTypes = {
    instanceId: PropTypes.string.isRequired,
    className: PropTypes.string
};

export default function ActivitySentimentWidget({instanceId, className, pdfExportConfig, appSpecificExportConfig}) {

    const {
        title = 'Activity and Sentiment',
        description,
        activityTitle,
        activityDescription,
        netSentimentTitle,
        netSentimentDescription
    } = listeningDefinitions[instanceId] || {};

    const metricFilters = useListeningCurationFilterValues(instanceId);

    const intervalSelector =
        <IntervalSelector
            instanceId={instanceId}
        />;
    const {selectedInterval} = useIntervalOptions(instanceId);
    const interval = useCurationConfigValue('graphInterval');

    const {showContentModal, ContentModal} = useContentModal(instanceId);

    const metricRequests = {
        activity: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: [interval],
            measure: 'count',
            filters: metricFilters
        },
        sentiment: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: [interval],
            measure: 'netSentiment',
            filters: metricFilters
        }
    };

    useSetExportConfig(instanceId, {
        title: title,
        supportedExportFormats: _.values(RivalIQ.enums.exportFormat),
        supportedExportTypes: _.values(RivalIQ.enums.exportType),
        pdfExportConfig,
        csvExportConfig: {
            type: 'dataSourceMetricsGenerator',
            metricGroups: [
                [{
                    dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
                    dimensions: [interval],
                    measure: {
                        field: 'netSentiment',
                        returns: `dimensionValue(${interval})`
                    },
                    filters: metricFilters
                }].concat(_.values(metricRequests))
            ]
        },
        appSpecificExportConfig
    });

    useRegisterSlideElementsForPPTExport(instanceId, {
        header: {
            title,
            subtitle: appSpecificExportConfig?.timePeriodStr
        }
    });


    return (
        <Panel
            instanceId={instanceId}
            title={title}
            description={description}
            exportSubtitle={appSpecificExportConfig?.timePeriodStr}
            showViewMore={true}
            className={className}
            additionalTitleBarControls={intervalSelector}
        >
            <MetricLoadingView
                instanceId={instanceId}
                metricRequests={metricRequests}>
                {
                    (metricResults) => {
                        const dataByInterval = RIQMetricLineChart.collateMetricResults(selectedInterval.id, metricRequests, metricResults, ['activity', 'sentiment']);

                        const activityLineColor = '#6a788c';
                        const sentimentLineColor = '#6a788c';
                        const chartConfigs = [{
                            chartName: `${activityTitle} per ${selectedInterval.name}`,
                            description: activityDescription,

                            metrics: [{
                                dataKey: 'activity',
                                lineColor: activityLineColor,
                                abbreviatedFormatter: RivalIQ.metrics.new.MetricFormatter.getFormatter(metricRequests.activity, metricResults.activity, RivalIQ.metrics.new.enums.MetricFormatTypes.abbreviated),
                                expandedFormatter: RivalIQ.metrics.new.MetricFormatter.getFormatter(metricRequests.activity, metricResults.activity, RivalIQ.metrics.new.enums.MetricFormatTypes.expanded),
                                metricRequest: metricRequests.activity
                            }]
                        }, {
                            area: true,
                            colorRules: {
                                positiveColor: '#8bb84f',
                                negativeColor: '#ef592f'
                            },
                            chartName: `${netSentimentTitle} per ${selectedInterval.name}`,
                            description: netSentimentDescription,
                            domainMax: 1,

                            metrics: [{
                                showAreaChart: true,
                                dataKey: 'sentiment',
                                lineColor: sentimentLineColor,
                                abbreviatedFormatter: RivalIQ.metrics.new.MetricFormatter.getFormatter(metricRequests.sentiment, metricResults.sentiment, RivalIQ.metrics.new.enums.MetricFormatTypes.abbreviated),
                                expandedFormatter: RivalIQ.metrics.new.MetricFormatter.getFormatter(metricRequests.sentiment, metricResults.sentiment, RivalIQ.metrics.new.enums.MetricFormatTypes.expanded),
                                metricRequest: metricRequests.sentiment
                            }]
                        }];

                        chartConfigs[0].tooltipContent = <LineChartTooltip
                            showContentModal={showContentModal}
                            chartConfigs={chartConfigs}
                        />;

                        return (
                            <>
                                <div className='tlw-p-7.5'>
                                    <RIQMetricLineChart
                                        key='line-chart'
                                        lineChartHeight={165}
                                        chartKey={instanceId}
                                        dataByInterval={dataByInterval}
                                        chartConfigs={chartConfigs}
                                    />
                                </div>

                                {ContentModal}
                            </>
                        );
                    }
                }
            </MetricLoadingView>
        </Panel>
    );
}
